import React, { useCallback, useEffect, useState } from 'react';
import { noop } from 'lodash';
import { BriefIcon } from '@fiverr-private/icons';
import { BriefDrawer } from '@fiverr-private/ai_brief';
import { useBusinessSignInSteps } from '@fiverr-private/identification_triggers';
import { getContext } from '@fiverr-private/fiverr_context';
import { BUSINESS_TRIGGER_CTA } from '@fiverr-private/business_success_sign_modal';
import { Container } from '@fiverr-private/layout_components';
import { useMonitoring } from '../../../../utils/monitoring';
import { useRelevantGigs } from '../../useRelevantGigs';
import { handleSignUpModalOpen } from '../../getGlobals';
import { GigData } from '../../../../server/types/GigData';
import { FederatedSeller } from '../../../../types';
import { ContactOptionRow } from './ContactOptionRow';

const T_PREFIX = 'seller_pages_toolbox.pro_contact.dropdown';
const COMPONENT_NAME = 'contact_me_dropdown';

interface AiBriefMenuItemProps {
    parentComponentOpen?: boolean;
    onClick?: () => void;
    options?: {
        priorInteraction?: boolean;
        source?: string;
        aiBriefInitialGenerationText?: string;
    };
    trigger?: React.ReactNode;
    onContactClick?: () => void;
    seller: FederatedSeller;
    gigs: GigData[];
}

export const AiBriefMenuItem = ({
    parentComponentOpen,
    onClick = noop,
    options,
    onContactClick = noop,
    trigger,
    seller,
    gigs,
}: AiBriefMenuItemProps) => {
    const [isShowDrawer, setIsShowDrawer] = useState(false);
    const [isDrawerLoading, setIsDrawerLoading] = useState(false);

    const sellerUsername = seller?.user?.name;
    const sellerId = seller?.user?.id;
    const sellerDisplayName = seller?.user?.profile?.displayName || sellerUsername;
    const responseTime = seller?.responseTime?.inHours;

    const { Providers, fireImpressionEvents, fireEvents } = useMonitoring();

    const mostRelevantGig = useRelevantGigs(gigs)?.[0];
    const { userId, organizationId } = getContext();

    const closeDrawer = () => {
        setIsShowDrawer(false);
        setIsDrawerLoading(false);
    };

    const handleContactClick = () => {
        closeDrawer();
        onContactClick();
        fireEvents({
            [Providers.BigQuery]: {
                type: 'business_seller_page.contact_me',
                event_source: options?.source,
                component_name: COMPONENT_NAME,
                page: {
                    component: {
                        name: COMPONENT_NAME,
                    },
                },
            },
            [Providers.Mixpanel]: {
                type: 'clicked contact me',
                source: options?.source,
                component_name: COMPONENT_NAME,
            },
        });
    };

    const sendBiImpressionEvent = useCallback(() => {
        fireImpressionEvents({
            [Providers.BigQuery]: {
                type: 'professional_brief_button_shown',
                group: 'user_impression',
                sub_group: 'fiverr_pro',
                user: {
                    id: userId,
                },
                seller: {
                    id: Number(sellerId),
                },
                organization: {
                    id: organizationId,
                },
                action: {
                    type: 'imp',
                },
                page: {
                    name: options?.source || '',
                    element: {
                        name: 'button',
                    },
                },
                feature: {
                    name: 'fiverr_pro',
                },
                gig: {
                    id: mostRelevantGig?.gig_id,
                },
                buyer: {
                    request: {
                        category_id: mostRelevantGig.category_id,
                        subcategory_id: mostRelevantGig.sub_category_id,
                    },
                },
                out_of_session: 'False',
            },
        });
    }, [Providers.BigQuery, fireImpressionEvents, mostRelevantGig, organizationId, sellerId, userId, options?.source]);

    useEffect(() => {
        if (parentComponentOpen) {
            sendBiImpressionEvent();
        }
    }, [sendBiImpressionEvent, parentComponentOpen]);

    const openDrawer = () => {
        if (!userId) {
            return handleSignUpModalOpen({
                source: options?.source || '',
                type: BUSINESS_TRIGGER_CTA.CONTACT,
                data: { sellerName: sellerDisplayName },
            });
        }

        setIsDrawerLoading(true);
        setIsShowDrawer(true);
    };

    const { signInSteps } = useBusinessSignInSteps({
        id: `brief-drawer-${sellerId}`,
        activationModalParams: {},
        onSuccess: openDrawer,
        signInModalParams: {
            source: options?.source || '',
            businessTriggerCtaType: BUSINESS_TRIGGER_CTA.CONTACT,
        },
        migrateModalParams: {
            source: options?.source || '',
            biEnrichment: {
                pageElementName: options?.source || '',
                trigger: BUSINESS_TRIGGER_CTA.NO_RESULT_PAGE_BRIEF,
            },
        },
    });

    const handleClick = () => {
        signInSteps();
        onClick();
        fireEvents({
            [Providers.Mixpanel]: {
                type: 'clicked on contact me option',
                ...options,
                option: 'Create a brief',
                sourceComponent: 'dropdown',
            },
        });
    };

    return (
        <>
            <Container onClick={handleClick} width="inherit">
                {trigger || (
                    <ContactOptionRow
                        renderIcon={BriefIcon}
                        translationRoute={`${T_PREFIX}.create_brief`}
                        onClick={handleClick}
                    />
                )}
            </Container>
            <BriefDrawer
                componentName={COMPONENT_NAME}
                source={options?.source || ''}
                seller={{
                    id: Number(sellerId),
                    userName: sellerUsername,
                    displayName: sellerDisplayName,
                    responseTime: responseTime ?? 0,
                }}
                initialText={options?.aiBriefInitialGenerationText}
                shouldRefreshData={isShowDrawer}
                categoryId={mostRelevantGig?.category_id}
                subCategoryId={mostRelevantGig?.sub_category_id}
                isOpen={isShowDrawer && !isDrawerLoading}
                onClose={closeDrawer}
                onLoadingChange={setIsDrawerLoading}
                onContactSellerClick={handleContactClick}
            />
        </>
    );
};
